import React, { FC, useMemo, useRef, useState } from 'react';

import { Table, Tabs } from '@silverfort/sf-components';

import useGetRefSize from '../../../../../hooks/useGetRefSize';
import { SortDirection } from '../../../../../consts/Sort';
import { ActivityEntity } from '../../../api/types/Insights';
import useGetColumns from './useGetColumns';
import { TABS } from './tabs';

interface Props {
    activityUsers: ActivityEntity[];
    activityResources: ActivityEntity[];
    activityPrefix: string;
    counterColumnTitle: string;
}

interface GetRowsParams {
    selectedTab: number;
    activityUsers: ActivityEntity[];
    activityResources: ActivityEntity[];
    sortColumn: string;
    sortDirection: string;
}

const getRows = ({ activityUsers, activityResources, sortDirection, sortColumn, selectedTab }: GetRowsParams) => {
    const rows = selectedTab === TABS.USERS_TAB.value ? activityUsers : activityResources;
    rows?.sort((a, b) => {
        if (sortDirection === 'asc') {
            return (a[sortColumn] ?? 0) > (b[sortColumn] ?? 0) ? 1 : -1;
        } else {
            return (b[sortColumn] ?? 0) > (a[sortColumn] ?? 0) ? 1 : -1;
        }
    });

    return rows;
};

const ActivityPopupContent: FC<Props> = ({ activityUsers, activityResources, activityPrefix, counterColumnTitle }) => {
    const tableRef = useRef(null);
    const [selectedTab, setSelectedTab] = useState(TABS.USERS_TAB.value);
    const [sortColumn, setSortColumn] = useState<keyof ActivityEntity>('count');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    const { height: tableHeight } = useGetRefSize(tableRef);
    const rows = getRows({
        activityUsers,
        activityResources,
        sortDirection,
        sortColumn,
        selectedTab
    });

    const columns = useGetColumns(selectedTab, counterColumnTitle, rows);
    const tabsWithPrefix = useMemo(
        () => Object.values(TABS).map((tab) => ({ ...tab, name: `${activityPrefix} ${tab.name}` })),
        [activityPrefix]
    );

    const handleTabClicked = (newSelectedTab: number) => {
        setSelectedTab(newSelectedTab);
    };

    const handleSort = (columnKey: keyof ActivityEntity, sortDirection: SortDirection) => {
        setSortColumn(columnKey);
        setSortDirection(sortDirection);
    };

    return (
        <div className="h-full w-full grid grid-rows-[48px_calc(100%-48px-16px)] gap-[16px]">
            <Tabs onClick={handleTabClicked} selectedTab={selectedTab} tabs={tabsWithPrefix} />
            <div className="h-full w-full" ref={tableRef}>
                <Table
                    columns={columns}
                    rows={rows}
                    bodyHeight={(tableHeight ?? 0) - 56}
                    tableContainerClassName="w-full h-full"
                    EmptyStateProps={{ title: 'No data', className: 'w-full max-h-[90%]' }}
                    onSortChange={handleSort}
                />
            </div>
        </div>
    );
};

export default ActivityPopupContent;
